import { defaultFilters, Filters } from '$lib/models/filters';
import type { Tag } from '$lib/models/tag';

/**
 * Returns true if any filter is selected that can be removed.
 * Currently, this is any filter except the order by filter.
 */
export function isAnyRemovableFilterSelected(filters: Filters): boolean {
  const count = filterCount(filters);
  return count > 1 || (count === 1 && filters.orderBy === defaultFilters.orderBy);
}

/**
 * Returns whether a single category filter is selected (i.e. a mood, genre or category) and nothing else.
 */
export function isSinglePrettyURLCategorySelected(filters: Filters): boolean {
  let numFilters = filterCount(filters);
  let isSingleSFXCategoryChild = false;
  if (filters.categories.length === 2) {
    const categoryA = filters.categories[0];
    const categoryB = filters.categories[1];
    isSingleSFXCategoryChild =
      categoryA.children.some((c) => c.id === categoryB.id) || categoryB.children.some((c) => c.id === categoryA.id);
  }

  return (numFilters === 1 && prettyURLCategoryCount(filters) === 1) || (numFilters === 2 && isSingleSFXCategoryChild);
}

/**
 * If a single category and subcategory filters are selected, i.e.: human -> applause, returns the subcategory.
 * Otherwise, returns null.
 */
export function maybeGetSingleSelectedChildCategory(filters: Filters): Tag | null {
  const filtersAmount = filterCount(filters);
  if (filtersAmount !== 2) {
    return null;
  }
  const categoryAmount = prettyURLCategoryCount(filters);
  if (categoryAmount !== 2) {
    return null;
  }

  if (filters.categories.length === 2) {
    // The order of the categories is not guaranteed, we don't know if A or B
    // is the parent or child category. We need to check both cases.
    const catA = filters.categories[0];
    const catB = filters.categories[1];
    const child = catA.children.find((c) => c.id === catB.id) || catB.children.find((c) => c.id === catA.id);
    if (child) {
      return child;
    }
  }

  return null;
}

/**
 * Returns the total count of filters selected which are different from default filters.
 */
export function filterCount(filters: Filters): number {
  if (!filters) {
    return 0;
  }
  let filterCount = 0;

  if (filters.orderBy !== defaultFilters.orderBy) filterCount++;
  if (filters.minBpm !== defaultFilters.minBpm) filterCount++;
  if (filters.maxBpm !== defaultFilters.maxBpm) filterCount++;
  if (filters.minLength !== defaultFilters.minLength) filterCount++;
  if (filters.maxLength !== defaultFilters.maxLength) filterCount++;
  if (filters.similarTo) filterCount++;

  return filterCount + prettyURLCategoryCount(filters) + categoryCount(filters);
}

/**
 * Returns the total count of category filters selected that use IDs.
 * Category filters include energy levels, instruments, themes and types.
 */
export function categoryCount(filters: Filters): number {
  let categoryCount = 0;

  if (filters.energyLevels.length > 0) categoryCount += filters.energyLevels.length;
  if (filters.types.length > 0) categoryCount += filters.types.length;

  return categoryCount;
}

/**
 * Returns the total count of category filters selected that use pretty URLs.
 * Category filters include moods, genres and categories.
 */
export function prettyURLCategoryCount(filters: Filters): number {
  let categoryCount = 0;

  if (filters.moods.length > 0) categoryCount += filters.moods.length;
  if (filters.genres.length > 0) categoryCount += filters.genres.length;
  if (filters.categories.length > 0) categoryCount += filters.categories.length;
  if (filters.instruments.length > 0) categoryCount += filters.instruments.length;
  if (filters.themes.length > 0) categoryCount += filters.themes.length;

  return categoryCount;
}

/**
 * Converts the search parameters from the URL to a Filters object.
 *
 * @param params The URL search parameters.
 * @param categorySlug The category slug from the URL (i.e 'rock' in /royalty-free-music/genre/rock)
 * @param subCategorySlug  The sub category slug from the URL (i.e 'applause' in /sound-effects/category/human/applause)
 * @param moods All available moods.
 * @param genres All available genres.
 * @param categories All available categories.
 */
export function searchToFilters(
  params: URLSearchParams,
  categorySlug: string | null,
  subCategorySlug: string | null,
  moods: Tag[] | undefined | null,
  genres: Tag[] | undefined | null,
  energyLevels: Tag[] | undefined | null,
  instruments: Tag[] | undefined | null,
  themes: Tag[] | undefined | null,
  types: Tag[] | undefined | null,
  categories: Tag[] | undefined | null
): Filters {
  const moodIds = (params.get('mood')?.split(',') || []).map((id) => parseInt(id));
  const genreIds = (params.get('genre')?.split(',') || []).map((id) => parseInt(id));
  const energyLevelIds = (params.get('energyLevel')?.split(',') || []).map((id) => parseInt(id));
  const instrumentIds = (params.get('instrument')?.split(',') || []).map((id) => parseInt(id));
  const themeIds = (params.get('theme')?.split(',') || []).map((id) => parseInt(id));
  const typeIds = (params.get('type')?.split(',') || []).map((id) => parseInt(id));
  const categoryIds = (params.get('category')?.split(',') || []).map((id) => parseInt(id));

  categorySlug = categorySlug || params.get('categorySlug');
  subCategorySlug = subCategorySlug || params.get('subCategorySlug');
  let categoryType = params.get('categoryType');

  return {
    moods: moods ? findSelectedTagsFromSearch(moods, moodIds, categoryType === 'mood' ? categorySlug : null, subCategorySlug) : [],
    genres: genres ? findSelectedTagsFromSearch(genres, genreIds, categoryType === 'genre' ? categorySlug : null, subCategorySlug) : [],
    energyLevels: energyLevels
      ? findSelectedTagsFromSearch(energyLevels, energyLevelIds, categoryType === 'energyLevel' ? categorySlug : null, subCategorySlug)
      : [],
    instruments: instruments
      ? findSelectedTagsFromSearch(instruments, instrumentIds, categoryType === 'instrument' ? categorySlug : null, subCategorySlug)
      : [],
    themes: themes ? findSelectedTagsFromSearch(themes, themeIds, categoryType === 'theme' ? categorySlug : null, subCategorySlug) : [],
    types: types ? findSelectedTagsFromSearch(types, typeIds, categoryType === 'type' ? categorySlug : null, subCategorySlug) : [],
    categories: categories
      ? findSelectedTagsFromSearch(categories, categoryIds, categoryType === 'category' ? categorySlug : null, subCategorySlug)
      : [],
    orderBy: params.get('orderBy') || defaultFilters.orderBy,
    minBpm: parseInt(params.get('minBpm') || defaultFilters.minBpm.toString()),
    maxBpm: parseInt(params.get('maxBpm') || defaultFilters.maxBpm.toString()),
    minLength: parseInt(params.get('minLength') || defaultFilters.minLength.toString()),
    maxLength: parseInt(params.get('maxLength') || defaultFilters.maxLength.toString()),
    similarTo: parseInt(params.get('similarTo') || '0'),
  };
}

/**
 * Converts the Filters object to URL search parameters.
 * When a single category is selected, the category slug is added to the search parameters instead of the ID.
 *
 * @param filters The current active filters.
 * @param page The current active page.
 * @param searchQuery The current search query in the header searchbar.
 * @returns
 */
export function filtersToSearch(filters: Filters, page = 1, searchQuery = '', prettyURL = true): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (isSinglePrettyURLCategorySelected(filters) && !searchQuery && prettyURL) {
    searchParams.set(
      'categorySlug',
      (filters.moods[0] || filters.genres[0] || filters.categories[0] || filters.instruments[0] || filters.themes[0]).slug
    );
    return searchParams;
  }

  if (searchQuery) {
    searchParams.set('search', searchQuery);
  }

  if (page > 1) {
    searchParams.set('page', page.toString());
  }

  if (filters.moods.length > 0) {
    searchParams.set('mood', filters.moods.map((m) => m.id).join(','));
  }

  if (filters.genres.length > 0) {
    searchParams.set('genre', filters.genres.map((g) => g.id).join(','));
  }

  if (filters.energyLevels.length > 0) {
    searchParams.set('energyLevel', filters.energyLevels.map((e) => e.id).join(','));
  }

  if (filters.instruments.length > 0) {
    searchParams.set('instrument', filters.instruments.map((i) => i.id).join(','));
  }

  if (filters.themes.length > 0) {
    searchParams.set('theme', filters.themes.map((t) => t.id).join(','));
  }

  if (filters.types.length > 0) {
    searchParams.set('type', filters.types.map((t) => t.id).join(','));
  }

  if (filters.categories.length > 0) {
    searchParams.set('category', filters.categories.map((c) => c.id).join(','));
  }

  if (filters.orderBy != defaultFilters.orderBy) {
    searchParams.set('orderBy', filters.orderBy);
  }

  if (filters.orderBy === 'title') {
    searchParams.set('order', 'asc');
  }

  if (filters.minBpm != defaultFilters.minBpm) {
    searchParams.set('minBpm', filters.minBpm.toString());
  }

  if (filters.maxBpm != defaultFilters.maxBpm) {
    searchParams.set('maxBpm', filters.maxBpm.toString());
  }

  if (filters.minLength != defaultFilters.minLength) {
    searchParams.set('minLength', filters.minLength.toString());
  }

  if (filters.maxLength != defaultFilters.maxLength) {
    searchParams.set('maxLength', filters.maxLength.toString());
  }

  if (filters.similarTo) {
    searchParams.set('similarTo', filters.similarTo.toString());
  }

  return searchParams;
}

/**
 * Returns an array of selected tags from the provided list of tags and updates the selected attribute for each tag.
 *
 * It compares the list of selected tags with the list of tags and sets the selected attribute to true or false.
 * Also sets the selected attribute to true for the parent tag if a child tag is selected.
 *
 * @param tags The list of tags to check.
 * @param selectedCategories The list of selected categories to check against.
 */

export function findSelectedTags(tags: Tag[], selectedCategories: Tag[] | null = null): Tag[] {
  const result = [];
  for (const parent of tags || []) {
    if (selectedCategories !== null) {
      parent.selected = selectedCategories.some((ic) => ic.id === parent.id);
    }

    if (parent.selected) {
      result.push(parent);
    }
    if (parent.children?.length) {
      for (const children of parent.children) {
        if (selectedCategories !== null) {
          children.selected = selectedCategories.some((ic) => ic.id === children.id);
        }
        if (children.selected) {
          if (!parent.selected) {
            parent.selected = true;
            result.push(parent);
          }
          result.push(children);
        }
      }
    }
  }
  return result;
}

/**
 * Finds and returns selected tags based on provided ids or slugs.
 * It checks both the parent categories and their children for a match.
 *
 * @param categories All available categories.
 * @param ids The list of selected IDs.
 * @param categorySlug The currently selected category slug.
 * @param subCategorySlug The currently selected sub category slug.
 */
function findSelectedTagsFromSearch(categories: Tag[], ids: number[], categorySlug: string | null, subCategorySlug: string | null): Tag[] {
  const selectedTags: Tag[] = [];

  for (const category of categories) {
    const categoryMatchedSlug = categorySlug && category.slug === categorySlug;
    const categoryMatchedId = ids.includes(category.id);

    if ((categoryMatchedSlug || categoryMatchedId) && !selectedTags.some((tag) => tag.id === category.id)) {
      selectedTags.push(category);
    }

    if (category.children) {
      for (const subCategory of category.children) {
        const subCategoryMatchedSlug = subCategorySlug && subCategory.slug === subCategorySlug;
        const subCategoryMatchedId = ids.includes(subCategory.id);

        if ((subCategoryMatchedSlug || subCategoryMatchedId) && !selectedTags.some((tag) => tag.id === subCategory.id)) {
          selectedTags.push(subCategory);
        }
      }
    }
  }

  return selectedTags;
}
